import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { T, FONT, MONO, radius, tint, alpha } from './poyais-ui/theme';
import { useIsMobile } from './poyais-ui/hooks';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'TikTok Ads Bağlı Değil': 'TikTok Ads Not Connected',
  'Ayarlar sayfasından TikTok Ads hesabınızı bağlayın.': 'Connect your TikTok Ads account on the Settings page.',
  'Ayarlara Git': 'Go to Settings',
  'Veri alınamadı': 'Could not load data',
  'Hata': 'Error',
  'HARCAMA': 'SPEND', 'TIKLAMA': 'CLICKS', 'GÖSTERİM': 'IMPRESSIONS', 'DURUM': 'STATUS',
  'SATIN ALMA': 'PURCHASES', 'GELİR': 'REVENUE', 'DÖNÜŞÜM': 'CONVERSIONS', 'AKTİF KAMPANYA': 'ACTIVE CAMPAIGNS',
  'önceki döneme göre': 'vs. previous period',
  'Günlük Trend': 'Daily Trend', 'Harcama': 'Spend', 'Tıklama': 'Clicks', 'Satın Alma': 'Purchases', 'Tarih': 'Date',
  'Kampanyalar': 'Campaigns', 'Reklam Grupları': 'Ad Groups',
  'kampanya': 'campaigns', 'reklam grubu': 'ad groups',
  'Dışa Aktar': 'Export',
  'KAMPANYA': 'CAMPAIGN', 'REKLAM GRUBU': 'AD GROUP', 'BÜTÇE': 'BUDGET',
  'Bu dönem için kampanya yok.': 'No campaigns for this period.',
  'Bu dönem için reklam grubu yok.': 'No ad groups for this period.',
  'Aktif': 'Active', 'Durduruldu': 'Paused', 'Silindi': 'Deleted',
  'günlük': 'daily', 'toplam': 'lifetime',
  'Gelir ve ROAS, TikTok pikselindeki satın alma (Complete Payment) olayından gelir; piksel kurulu değilse boş görünür.':
    'Revenue and ROAS come from the TikTok pixel purchase (Complete Payment) event; they stay empty without the pixel.',
});

const API = '/api/v1';

function fmtN(v: number) { return Math.round(Number(v || 0)).toLocaleString('tr-TR'); }
function fmtPct(v: number) { return `%${Number(v || 0).toFixed(2)}`; }
function moneyFmt(currency: string) {
  let f: Intl.NumberFormat;
  try { f = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY', maximumFractionDigits: 0 }); }
  catch { f = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }); }
  return (v: number) => f.format(Number(v || 0));
}
const changePct = (cur: number, prev: number) => prev ? Math.round((cur - prev) / prev * 1000) / 10 : undefined;

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

function ErrorBox({ error }: { error: string }) {
  const { t } = useLang();
  return <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}><div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div><div style={{ fontSize: 12, color: T.textMid }}>{error}</div></div>;
}

function DisconnectedState() { const { t } = useLang(); return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14, fontFamily: FONT }}><div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t('TikTok Ads Bağlı Değil')}</div><div style={{ fontSize: 13, color: T.textDim }}>{t('Ayarlar sayfasından TikTok Ads hesabınızı bağlayın.')}</div><Button variant="solid" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'settings' }))}>{t('Ayarlara Git')}</Button></div>); }

function useTikTok(path: string, clientId: number, start?: string, end?: string) {
  const { t } = useLang();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  useEffect(() => {
    setLoading(true); setError(null); setData(null);
    axios.get(`${API}/tiktok/${clientId}/${path}`, { params: { start_date: start || monthStart, end_date: end || today } })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || t('Veri alınamadı')); setLoading(false); });
  }, [path, clientId, start, end]);
  return { data, loading, error };
}

// --- GENEL BAKIŞ ---
function OverviewTab({ clientId, currency, start, end }: { clientId: number; currency: string; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const { data, loading, error } = useTikTok('overview', clientId, start, end);
  const [chartMetric, setChartMetric] = useState<'spend' | 'clicks' | 'purchases'>('spend');
  const money = moneyFmt(currency);

  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;

  const prev = data?.prev || {};
  const daily = data?.daily || [];
  const changeColor = (v: number) => v > 0 ? T.greenText : v < 0 ? T.redText : T.textDim;
  const kpis = [
    { label: t('HARCAMA'), value: money(data?.spend), change: changePct(data?.spend, prev.spend), color: T.accent },
    { label: t('GÖSTERİM'), value: fmtN(data?.impressions), change: changePct(data?.impressions, prev.impressions), color: T.textDim },
    { label: t('TIKLAMA'), value: fmtN(data?.clicks), change: changePct(data?.clicks, prev.clicks), color: T.blue },
    { label: 'CTR', value: fmtPct(data?.ctr), color: T.textDim },
    { label: 'CPC', value: money(data?.cpc), color: T.textDim },
    { label: 'CPM', value: money(data?.cpm), color: T.textDim },
    { label: t('DÖNÜŞÜM'), value: fmtN(data?.conversions), change: changePct(data?.conversions, prev.conversions), color: T.textDim },
    { label: t('SATIN ALMA'), value: fmtN(data?.purchases), change: changePct(data?.purchases, prev.purchases), color: T.greenText },
    { label: t('GELİR'), value: data?.revenue ? money(data.revenue) : '—', change: data?.revenue ? changePct(data.revenue, prev.revenue) : undefined, color: T.greenText },
    { label: 'ROAS', value: data?.roas ? `${Number(data.roas).toFixed(2)}x` : '—', color: T.textDim },
    { label: t('AKTİF KAMPANYA'), value: fmtN(data?.campaigns_count), color: T.textDim },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Grid min={isMobile ? 130 : 150} gap={isMobile ? 8 : 14}>
        {kpis.map((k, i) => (
          <Card key={i} pad={18}>
            <Eyebrow color={k.color}>{k.label}</Eyebrow>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: MONO, fontVariantNumeric: 'tabular-nums', marginTop: 8 }}>{k.value}</div>
            {k.change !== undefined && (
              <div style={{ fontSize: 11, fontWeight: 700, color: changeColor(k.change), marginTop: 4 }}>
                {k.change > 0 ? '↑' : k.change < 0 ? '↓' : '·'} %{Math.abs(k.change)} {t('önceki döneme göre')}
              </div>
            )}
          </Card>
        ))}
      </Grid>
      {!data?.revenue && (
        <div style={{ fontSize: 11.5, color: T.textDim }}>{t('Gelir ve ROAS, TikTok pikselindeki satın alma (Complete Payment) olayından gelir; piksel kurulu değilse boş görünür.')}</div>
      )}
      {daily.length > 0 && (
        <Card pad={20}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const, gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t('Günlük Trend')}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['spend', 'clicks', 'purchases'] as const).map(k => (
                <Button key={k} variant={chartMetric === k ? 'soft' : 'ghost'} color={T.accent} onClick={() => setChartMetric(k)}
                  style={{ padding: '4px 12px', fontSize: 10, fontWeight: chartMetric === k ? 700 : 400 }}>
                  {k === 'spend' ? t('Harcama') : k === 'clicks' ? t('Tıklama') : t('Satın Alma')}
                </Button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={v => (v || '').slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: any) => chartMetric === 'spend' ? money(Number(v)) : fmtN(Number(v))} labelFormatter={l => `${t('Tarih')}: ${l}`} />
              <Line type="monotone" dataKey={chartMetric} stroke={T.accent} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// --- KAMPANYALAR / REKLAM GRUPLARI ---
function EntityTable({ clientId, currency, start, end, kind }: { clientId: number; currency: string; start?: string; end?: string; kind: 'campaigns' | 'adgroups' }) {
  const { t } = useLang();
  const { data, loading, error } = useTikTok(kind, clientId, start, end);
  const money = moneyFmt(currency);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const rows: any[] = data || [];
  const isCamp = kind === 'campaigns';

  const statusBadge = (s: string) => {
    if (s === 'ENABLED') return { text: t('Aktif'), color: T.greenText };
    if (s === 'PAUSED') return { text: t('Durduruldu'), color: T.amberText };
    if (s === 'DELETED') return { text: t('Silindi'), color: T.textDim };
    return { text: s || '—', color: T.textDim };
  };
  const budget = (r: any) => r.daily_budget ? `${money(r.daily_budget)} ${t('günlük')}` : r.total_budget ? `${money(r.total_budget)} ${t('toplam')}` : '—';

  const handleExport = () => {
    exportCsv(`tiktok-${isCamp ? 'kampanyalar' : 'reklam-gruplari'}-${start || ''}-${end || ''}.csv`, rows, [
      { key: 'name', label: t(isCamp ? 'KAMPANYA' : 'REKLAM GRUBU') },
      ...(isCamp ? [] : [{ key: 'campaign_name', label: t('KAMPANYA') }]),
      { key: 'status', label: t('DURUM') },
      { key: 'spend', label: t('HARCAMA') },
      { key: 'impressions', label: t('GÖSTERİM') },
      { key: 'clicks', label: t('TIKLAMA') },
      { key: 'ctr', label: 'CTR' },
      { key: 'cpc', label: 'CPC' },
      { key: 'purchases', label: t('SATIN ALMA') },
      { key: 'revenue', label: t('GELİR') },
      { key: 'roas', label: 'ROAS' },
      { key: 'daily_budget', label: t('BÜTÇE') },
    ]);
  };

  const heads = [isCamp ? 'KAMPANYA' : 'REKLAM GRUBU', 'DURUM', 'HARCAMA', 'GÖSTERİM', 'TIKLAMA', 'CTR', 'CPC', 'SATIN ALMA', 'ROAS', 'BÜTÇE'];
  const num = { padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const };

  return (
    <Card pad={0}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t(isCamp ? 'Kampanyalar' : 'Reklam Grupları')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 11, color: T.textDim }}>{rows.length} {t(isCamp ? 'kampanya' : 'reklam grubu')}</div>
          <Button variant="ghost" color={T.accent} disabled={rows.length === 0} onClick={handleExport} style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.download size={13} />{t('Dışa Aktar')}
          </Button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' as const }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth: 860 }}>
          <thead style={{ borderBottom: `1px solid ${T.border}`, background: T.card }}>
            <tr>
              {heads.map((h, i) => (
                <th key={i} style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '0.08em', textAlign: i === 0 ? 'left' as const : 'right' as const, whiteSpace: 'nowrap' as const }}>{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={heads.length} style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t(isCamp ? 'Bu dönem için kampanya yok.' : 'Bu dönem için reklam grubu yok.')}</td></tr>
            ) : rows.map((r: any) => {
              const sb = statusBadge(r.status);
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${T.card}` }} onMouseEnter={e => (e.currentTarget.style.background = T.card)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={r.name}>{r.name}</div>
                    {!isCamp && r.campaign_name && <div style={{ fontSize: 10.5, color: T.textDim, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.campaign_name}</div>}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const }}><Badge color={sb.color}>{sb.text}</Badge></td>
                  <td style={{ ...num, fontSize: 12, fontWeight: 600, color: T.text }}>{money(r.spend)}</td>
                  <td style={num}>{fmtN(r.impressions)}</td>
                  <td style={num}>{fmtN(r.clicks)}</td>
                  <td style={num}>{fmtPct(r.ctr)}</td>
                  <td style={num}>{money(r.cpc)}</td>
                  <td style={num}>{fmtN(r.purchases)}</td>
                  <td style={num}>{r.roas ? `${Number(r.roas).toFixed(2)}x` : '—'}</td>
                  <td style={num}>{budget(r)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// --- MAIN EXPORT ---
export default function TikTokAdsTab({ config, tab = 'overview', start, end }: { config: any; tab?: string; start?: string; end?: string }) {
  const clientId = config?.client_id;
  const tk = (config?.platforms as any)?.tiktok || {};
  if (!config?.platforms) return <Loader />;
  if (!clientId || !tk.advertiserId || tk.enabled === false) return <DisconnectedState />;
  const currency = tk.currency || 'TRY';
  if (tab === 'campaigns') return <EntityTable kind="campaigns" clientId={clientId} currency={currency} start={start} end={end} />;
  if (tab === 'adgroups') return <EntityTable kind="adgroups" clientId={clientId} currency={currency} start={start} end={end} />;
  return <OverviewTab clientId={clientId} currency={currency} start={start} end={end} />;
}
